import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { useSession } from '../../auth';
import { trackFailed } from '../../../../../shared-web/track';
import EmberLockup from '../../../../../shared-web/EmberLockup';
import { apiFailureReason, emberApi, type HomeResponse, type HowItWorks, type HowStep, type TreeYouResponse } from '../api';
import { Link } from '../components/Link';
import { NavAccountButton } from '../components/NavAccountButton';
import { NavAppBadge } from '../components/NavAppBadge';
import { Announcements } from '../components/Announcements';
import { InboundRegistrationCTA } from '../components/InboundRegistrationCTA';
import { JoinEmberCloser } from '../components/JoinEmberCloser';
import { ProfileGlyph } from '../components/ProfileGlyph';
import { cachedHome, cacheHome, loadHome } from '../home-page-cache';
import { currentTrack } from '../tracks';
import SiteFooter from '../../../../../shared-web/SiteFooter';
import { TreeHome } from '../tree/TreeHome';
import { ConnectionShot } from './ConnectionShots';
import { WhyShot } from './WhyShots';
import './Landing.css';
import './LandingPaper.css';

/**
 * S01 — the Banyan first screen (v4.0 §4.17, 2026-09-05) over the 2026-08-25
 * editorial lower half. The first screen is the tree: the viewer under it,
 * the lights in its canopy, the one door. Below, the permission model, How it
 * works, the contact closer, and the footer continue on paper.
 */
export function LandingPage({ initialHome = null, initialLightId = null }: {
  initialHome?: HomeResponse | null;
  /** `/?light=<id>` — open this light's paper on arrival. */
  initialLightId?: string | null;
} = {}): ReactElement {
  // The homepage track (tracks.ts): a payload read for another track — or the
  // plain one when the tab is on a track — is not this page's. The server's
  // own payload is always the first render all the same (hydration must see
  // what the server drew — on the server currentTrack() cannot read the tab,
  // and 2026-10-06 the first hours of /?t= hydrated against a null home and
  // threw React #418/#423 on every track landing); an off-track payload is
  // replaced right after mount.
  const track = currentTrack();
  const onTrack = (h: HomeResponse | null): HomeResponse | null => (h && (h.track ?? null) === track ? h : null);
  const [home, setHome] = useState<HomeResponse | null>(() => initialHome ?? onTrack(cachedHome()));
  const [failed, setFailed] = useState(false);
  // The footer's Sign in is for the signed-out. The server and the first
  // client paint start from the same bootstrap session, so they agree.
  const { status } = useSession();

  useEffect(() => {
    let alive = true;
    // The document brought this page's own payload (SSR): it is the page,
    // and reading it again a moment later drew the same tree twice
    // (2026-09-25). A client-side return to the homepage refreshes it.
    if (initialHome && (initialHome.track ?? null) === track) { cacheHome(initialHome); return () => { alive = false; }; }
    const renderableHome = onTrack(cachedHome());
    loadHome(() => emberApi.home({ track }))
      .then((next) => { if (alive) setHome(next); })
      .catch((err: unknown) => {
        if (!alive || renderableHome) return;
        trackFailed('home_load', 'hero_failed', { error_detail: apiFailureReason(err) });
        setFailed(true);
      });
    return () => { alive = false; };
  }, [initialHome]);

  return (
    <div className="landing">
      <div className="landing-artboard">
        <section className="landing-first landing-first-tree" aria-labelledby="landing-title">
          {/* v4.0 §4.17 (2026-09-05 拍板): the first screen is the Banyan. The
              hero photo, its wash and the frozen cqw type scale are retired
              with it (Landing.css keeps the rules for the lower half only).
              The ground carries the door (2026-09-12: the composer — the first
              sentence typed there is the first turn; TreeHome / HeroComposer). */}
          <TreeHome
            home={home}
            failed={failed}
            initialLightId={initialLightId}
            nav={(search, yourEmber, hasApp) => (
              <>
                <LandingNav onTree extra={search} yourEmber={yourEmber} hasApp={hasApp} />
              </>
            )}
          />

          {/* Below the tree (2026-09-12): first one statement of what is
              different here (why_ember); then How it works as one flow — the
              two steps every conversation comes to, a fork, and the four that
              sometimes follow (§0.8 E). Then the FAQ and the contact closer.
              The permission model is on the door itself (the hero's trust
              line), so the paper no longer opens with it. */}
          <div className="landing-lower">
            <WhyEmber why={home?.hero.why_ember} />
            <section className="landing-how" aria-labelledby="landing-how-title">
              <header className="landing-section-heading" data-log-seen="landing_section" data-log-state="how_it_works">
                <h2 id="landing-how-title">How it works</h2>
                {home ? <p>{home.hero.how_it_works.sub}</p> : null}
              </header>
              {home ? <HowFlow how={home.hero.how_it_works} /> : null}
            </section>
            <LandingFaq items={home?.faq || []} />
            <JoinEmberCloser returnTo="/" logName="landing_register_open" discovery="closer" />
          </div>
        </section>
      </div>

      <SiteFooter theme="light" signedIn={status === 'authenticated'} />
    </div>
  );
}

/**
 * The second screen (2026-09-13 拍板, evening): four differences, each one
 * card — a picture of the real thing the reader will meet (Ember's reply
 * over the pages others told here, an exchange in which the person's own
 * words get clearer, the page a conversation leaves, another person's page
 * with the word sent back: WhyShots) standing on the card's picture floor,
 * then a small label in the How it works register and one paragraph. Same
 * section grammar as the sections under it (a section title, one line under
 * it, then the content); no rail and no numbers, because these are four
 * sides of one thing, not a sequence. The card gives each block its edges:
 * without them the four pictures blurred into one another across the row
 * (the evening's first cut). Copy rides the home payload (content.js),
 * never here.
 */
function WhyEmber({ why }: { why: HomeResponse['hero']['why_ember'] | undefined }): ReactElement | null {
  if (!why) return null;
  return (
    <section className="landing-why" aria-labelledby="landing-why-title" data-log-seen="landing_section" data-log-state="why_ember">
      <header className="landing-section-heading">
        <h2 id="landing-why-title">{why.eyebrow}</h2>
        <p>{why.text}</p>
      </header>
      <ul className="landing-why-cards">
        {why.differences.map((d) => (
          <li key={d.shot}>
            <div className="landing-why-pic"><WhyShot shot={d.shot} /></div>
            <div className="landing-why-text">
              <h3>{d.label}</h3>
              <p>{d.text}</p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The FAQ under How it works (2026-09-10).
 *
 * Every answer is in the markup from the first paint — the list rides the home
 * payload, server-rendered — because the FAQPage JSON-LD on `/` describes this
 * block, and schema for text a visitor cannot see is the violation that got the
 * node removed on 2026-09-07. So: no accordion, no fetch of its own, and no
 * second copy of the wording. Empty list renders nothing at all, which is also
 * what the schema does.
 */
function LandingFaq({ items }: {
  items: { question: string; answer: string }[];
}): ReactElement | null {
  if (items.length === 0) return null;
  return (
    <section className="landing-faq" aria-labelledby="landing-faq-title">
      <header className="landing-section-heading" data-log-seen="landing_section" data-log-state="faq">
        <h2 id="landing-faq-title">Questions about Ember</h2>
      </header>
      {/* h3 + p rather than dl/dt/dd: a dt may not contain heading content,
          and the question is a heading — it is what the schema names. */}
      <div className="landing-faq-list">
        {items.map((item) => (
          <article className="landing-faq-item" key={item.question}>
            <h3>{item.question}</h3>
            <p>{item.answer}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function LandingNav({ onTree = false, extra = null, yourEmber = null, hasApp = null }: { onTree?: boolean; extra?: ReactNode; yourEmber?: TreeYouResponse['your_ember']; hasApp?: boolean | null } = {}): ReactElement {
  // A visitor's way back to what they told (2026-09-06 拍板): the Result page
  // is their Ember's page, and until now nothing on the site led back to it.
  // Only after this browser has told; signed in, the notebook is that door,
  // and at registration the Ember moves into it (mergeVisitorIntoUser).
  // A text telling's Ember lives in its thread (no Result page since 2026-09-12): its door is the chat.
  const yourEmberHref = !yourEmber ? null
    : yourEmber.finalized ? `/result/${encodeURIComponent(yourEmber.conversation_id)}?returnTo=%2F`
      : '/talk?mode=chat&returnTo=%2F';
  const { status } = useSession();

  return (
    <header className={`landing-nav${onTree ? ' landing-nav-on-tree' : ''}`}>
      <Link to="/" className="landing-brand" aria-label="Ember — home" data-log="nav_logo">
        <EmberLockup className="landing-brand-lockup" />
        {/* Product stage, not a sentence: the one word that makes a later
            "beta ended" read as that rather than "they started charging"
            (2026-09-12, §0.8 E). */}
        <span className="landing-brand-beta">beta</span>
      </Link>
      {/* What's new (2026-09-14 拍板): the team's horn, a small amber ornament
          under the wordmark in the empty top-left sky the canopy never reaches
          (the row itself is full on a phone) — product news for everyone, a
          red dot until this browser has opened it. */}
      <Announcements />
      {/* The app button under the right end (2026-10-26 Riley), for a person without the app. */}
      <NavAppBadge hasApp={hasApp} />
      {/* Desktop only. A phone has no menu for these (2026-10-21 founder):
          every row the phone menu held was already on the page — the composer
          is the way in, How it works is further down, Join Ember signs in a
          number that has an account — so the person's own circle sits at the
          right edge. The links stay in the markup, so /blog keeps its inbound
          link from the homepage. */}
      <nav className="landing-nav-links" aria-label="Homepage">
        <a href="#landing-how-title" data-log="nav_how_it_works">How it works</a>
        {/* v4.0 (2026-09-04): no Explore questions, no Themes, no Search — the
            product has no discovery surface (§0.3).

            Blog is here rather than in the footer (2026-09-06 拍板). It came
            off the nav earlier the same day on the belief that it was empty; it
            was not — three articles were live and indexed, and taking the link
            down left them, the use-case hub and the five /for/ pages with no
            inbound link from anywhere on the site. The homepage is the strongest
            page there is to link them from, so the link belongs here. A full
            document, not a router link: /blog is served by the marketing
            renderer, not the SPA. */}
        <a href="/blog" data-log="nav_blog">Blog</a>
        {status === 'anonymous' && yourEmberHref ? (
          <Link to={yourEmberHref} data-log="nav_your_ember">Your Ember</Link>
        ) : null}
      </nav>
      <nav className="landing-nav-actions" aria-label="Site">
        {extra}
        {/* No install icon up here (2026-09-05 walkthrough: a download glyph
            among four identical circles meant nothing to a first visit); the
            Take Ember with you card and the notebook's foot carry installing. */}
        {/* Same rule as the shared TopNav (2026-08-16): while the session is
            still loading, render nothing — a link whose target flips a beat
            later reads worse than a beat of blank. */}
        {status === 'loading' ? null : status === 'authenticated' ? (
          /* Signed in, the circle is the person's own animal (2026-09-05):
             the same glyph as the sign-in door said nothing about state.
             NavAccountButton since 2026-10-01 — the light's page docks it too;
             since 2026-10-21 with Chats and the notebook as doors beside it,
             the row the apps draw. */
          <NavAccountButton />
        ) : (
          /* Visitors (2026-09-02 拍板, §4.1; phones too since 2026-09-06): a quiet
             Join Ember pill where people who came to join already look — the
             same inbound door as the closer below, docked in place. Until 09-06
             a phone showed a bare person icon to /auth here instead: 41 people
             tapped it in a fortnight and nothing on it said what it was. */
          <InboundRegistrationCTA
            className="landing-nav-button landing-nav-join"
            icon={<ProfileGlyph />}
            label="Join Ember"
            returnTo="/"
            logName="landing_register_open"
            discovery="nav"
          />
        )}
      </nav>
    </header>
  );
}

/**
 * How it works as one flow (2026-09-12 拍板, later): the three text steps and
 * the drawn five-step card had said the same things twice, so they are one
 * rail. The two steps every conversation comes to; a dashed fork labelled
 * `Sometimes, later` — connection is conditional, and the break in the rail
 * is how the page says so; then the four steps that follow when it happens,
 * numbered on from the first two because it is one sequence. Every step
 * shows the surface where it happens (ConnectionShots). Both ends of the
 * line are the visitor: their steps fill the dot, Ember's stay hollow. No
 * seen-event of its own: the section heading carries the exposure.
 */
function HowFlow({ how }: { how: HowItWorks }): ReactElement {
  const offset = how.always.steps.length;
  return (
    <>
      <p className="landing-flow-label">{how.always.label}</p>
      <ol className="landing-flow landing-flow-always">
        {how.always.steps.map((step, i) => <FlowStep key={step.shot} step={step} number={i + 1} />)}
      </ol>
      <div className="landing-flow-fork">
        <span className="landing-flow-fork-line" aria-hidden="true" />
        <p className="landing-flow-label">{how.sometimes.label}</p>
        <span className="landing-flow-fork-line" aria-hidden="true" />
      </div>
      <p className="landing-flow-fork-sub">{how.sometimes.sub}</p>
      <ol className="landing-flow landing-flow-sometimes" start={offset + 1}>
        {how.sometimes.steps.map((step, i) => <FlowStep key={step.shot} step={step} number={offset + i + 1} />)}
      </ol>
      <p className="landing-flow-foot">{how.foot}</p>
    </>
  );
}

function FlowStep({ step, number }: { step: HowStep; number: number }): ReactElement {
  return (
    <li className={step.who === 'You' ? 'landing-flow-you' : undefined}>
      <span className="landing-flow-dot" aria-hidden="true">{number}</span>
      <div className="landing-flow-shot"><ConnectionShot shot={step.shot} /></div>
      <div className="landing-flow-text">
        <span className="landing-flow-who">{step.who}</span>
        <span>{step.text}</span>
        {step.gate ? (
          <span className="landing-flow-gate">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12l5 5L20 7" /></svg>
            {step.gate}
          </span>
        ) : null}
      </div>
    </li>
  );
}


